import React, { useState } from 'react';
import { Paper, Box, Button, TextField, CircularProgress, Alert } from '@mui/material';
import { GroupRounded, PeopleAltRounded, TrendingUpRounded } from '@mui/icons-material';
import { TabBar, FilterBar, Field, SelectInput, SectionCard, Grid, useTab } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import { useProviderReports, useReportBusinesses } from '../../hooks/useReports';
import { money, utilPct, statusLabel } from '../../utils/format';

function ReportCard(props: { columns: Column<any>[]; rows: any[]; title: string; exportName?: string; search?: boolean }) {
  return (
    <Paper sx={{ p: 0 }}>
      <DataTable columns={props.columns} rows={props.rows} title={props.title} exportName={props.exportName} search={props.search} pageSize={6} />
    </Paper>
  );
}

const ProviderReportsPage: React.FC = () => {
  const [businessId, setBusinessId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const ranged = !!(from && to);
  const { data, loading, error, reload } = useProviderReports(businessId || undefined, ranged ? from : undefined, ranged ? to : undefined);
  const { data: businesses } = useReportBusinesses();
  const [tab, setTab] = useTab('ops');

  const businessOpts = [{ value: '', label: 'All businesses' }, ...((businesses || []).map((b) => ({ value: b.id, label: b.name })))];
  const ret = (data?.retention as any) || {};

  return (
    <>
      <FilterBar>
        <Field label="Business"><SelectInput value={businessId} onChange={setBusinessId} options={businessOpts} minWidth={210} /></Field>
        <Field label="From"><TextField type="date" value={from} onChange={(e) => setFrom(e.target.value)} size="small" inputProps={{ max: to || undefined }} /></Field>
        <Field label="To"><TextField type="date" value={to} onChange={(e) => setTo(e.target.value)} size="small" inputProps={{ min: from || undefined }} /></Field>
        <Field label="&nbsp;"><Button variant="outlined" size="small" onClick={() => { setFrom(''); setTo(''); }} disabled={!from && !to} sx={{ height: 40 }}>All time</Button></Field>
      </FilterBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>
      ) : error || !data ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'No data.'}</Alert>
      ) : (
      <>
      <TabBar active={tab} onChange={setTab}
        tabs={[{ key: 'ops', label: 'Operations' }, { key: 'customers', label: 'Customers' }, { key: 'revenue', label: 'Revenue' }, { key: 'trends', label: 'Trends' }]} />

      {tab === 'ops' && (
        <Grid cols={2}>
          <ReportCard title="Daily Token Report" search rows={data.daily_tokens} columns={[
            { key: 'number', label: 'Token #' }, { key: 'customer', label: 'Customer' }, { key: 'service', label: 'Service' },
            { key: 'status', label: 'Status', csv: (r) => r.status, render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> }, { key: 'time', label: 'Time' }, { key: 'date', label: 'Date' },
          ]} />
          <ReportCard title="Queue Utilization Report" rows={data.queue_utilization} columns={[
            { key: 'queue', label: 'Queue' }, { key: 'capacity', label: 'Capacity', align: 'right' }, { key: 'booked', label: 'Booked', align: 'right' },
            { key: 'pct', label: 'Utilization %', align: 'right', csv: (r) => utilPct(r.booked, r.capacity), render: (r) => utilPct(r.booked, r.capacity) + '%' },
          ]} />
          <ReportCard title="Provider Performance Report" rows={data.provider_performance} columns={[
            { key: 'provider', label: 'Provider' }, { key: 'served', label: 'Customers Served', align: 'right' },
            { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
          ]} />
          <ReportCard title="Cancellation Report" rows={data.cancellations} columns={[
            { key: 'date', label: 'Date' }, { key: 'customer', label: 'Customer' }, { key: 'reason', label: 'Reason' },
          ]} />
          <ReportCard title="No Show Report" rows={data.no_shows} columns={[
            { key: 'customer', label: 'Customer' }, { key: 'count', label: 'No Show Count', align: 'right' },
          ]} />
        </Grid>
      )}

      {tab === 'customers' && (
        <Grid cols={2}>
          <ReportCard title="Customer Visit Report" search rows={data.customer_visits} columns={[
            { key: 'customer', label: 'Customer' }, { key: 'visits', label: 'Visit Count', align: 'right' }, { key: 'last', label: 'Last Visit' },
          ]} />
          <ReportCard title="Top Customer Report" rows={data.top_customers} columns={[
            { key: 'customer', label: 'Customer' }, { key: 'visits', label: 'Visit Count', align: 'right' },
          ]} />
          <ReportCard title="Follow-Up Due Report" rows={data.follow_ups} columns={[
            { key: 'customer', label: 'Customer' }, { key: 'due', label: 'Due Date' },
          ]} />
          <ReportCard title="Customer Growth Report" rows={data.customer_growth} columns={[
            { key: 'month', label: 'Month' }, { key: 'newCustomers', label: 'New Customers', align: 'right' },
          ]} />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <SectionCard title="Customer Retention">
              <Grid cols={3}>
                <StatCard icon={<GroupRounded />} color="#076FF7" value={ret.new_customers ?? 0} label="New Customers" />
                <StatCard icon={<PeopleAltRounded />} color="#10B981" value={ret.returning_customers ?? 0} label="Returning Customers" />
                <StatCard icon={<TrendingUpRounded />} color="#7C3AED" value={ret.retention_rate ?? '0%'} label="Retention Rate" />
              </Grid>
            </SectionCard>
          </Box>
        </Grid>
      )}

      {tab === 'revenue' && (
        <>
          <Box sx={{ mb: 2 }}>
            <ReportCard title="Revenue Report" exportName="revenue-report" rows={data.revenue_by_period} columns={[
              { key: 'period', label: 'Period' },
              { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
              { key: 'txns', label: 'Transactions', align: 'right' },
              { key: 'avg', label: 'Avg / Txn', align: 'right', csv: (r) => (r.txns ? Math.round(r.revenue / r.txns) : 0), render: (r) => money(r.txns ? Math.round(r.revenue / r.txns) : 0) },
            ]} />
          </Box>
          <ReportCard title="Service Revenue Report" exportName="service-revenue" rows={data.service_revenue} columns={[
            { key: 'service', label: 'Service' }, { key: 'txns', label: 'Transactions', align: 'right' },
            { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
          ]} />
        </>
      )}

      {tab === 'trends' && (
        <Grid cols={2}>
          <ReportCard title="Peak Hour Report" rows={data.peak_hours} columns={[
            { key: 'hour', label: 'Hour' }, { key: 'count', label: 'Customer Count', align: 'right' },
          ]} />
          <ReportCard title="Peak Day Report" rows={data.peak_days} columns={[
            { key: 'day', label: 'Day' }, { key: 'count', label: 'Customer Count', align: 'right' },
          ]} />
        </Grid>
      )}
      </>
      )}
    </>
  );
};

export default ProviderReportsPage;
